import { forwardRef, Ref } from "react";

import RecordNameCell, { ImageFieldPresentation } from "../../RecordNameCell";
import useMetadataValue from "../../../../hooks/useMetadataValue";
import AppSpinner from "../../../../elements/AppSpinner";
import AppTooltip from "../../../../elements/AppTooltip";
import useModule from "../../../../hooks/useModule";
import ReferenceTooltip from "../ReferenceTooltip";
import SubitemDisplay from "./SubitemDisplay";
import EmptyField from "../../EmptyField";

interface SingleReferenceRecordProps {
  onOpen: () => void;
  onView: (data: any) => void;
  existingValue: Record<string, any>;
  imageField?: string;
  imageFieldPresentation: ImageFieldPresentation;
  metadataField: string | null;
  metadataFieldType: string | null;
  submitting?: boolean;
  padding?: boolean;
  tableView: boolean;
  className?: string;
  disableViewRecord?: boolean;
}

const SingleReferenceRecord = (
  {
    onOpen,
    onView,
    existingValue,
    imageField,
    imageFieldPresentation,
    metadataField,
    metadataFieldType,
    submitting,
    padding = true,
    tableView,
    className,
    disableViewRecord = true,
  }: SingleReferenceRecordProps,
  ref: Ref<HTMLDivElement>
) => {
  const { module } = useModule(existingValue?._ModuleAPIName);
  const metadata = useMetadataValue(
    existingValue,
    metadataField,
    metadataFieldType
  );

  const recordNameField = module?.Templates?.RecordListView?.RecordNameField;
  const noRecordNameValue = module?.Templates?.RecordListView?.RecordNameEmpty;

  if (submitting || !existingValue) {
    return (
      <div ref={ref} className={`${padding ? "pr-4" : ""}`}>
        {submitting ? (
          <AppSpinner size="sm" />
        ) : (
          <EmptyField table={tableView} />
        )}
      </div>
    );
  }

  if (existingValue?._isSubitem) {
    return (
      <SubitemDisplay
        module={module}
        existingValue={existingValue}
        ref={ref}
        onOpen={onOpen}
        onView={onView}
      />
    );
  }

  return (
    <div ref={ref} className={className}>
      <AppTooltip
        content={(ref: any) => (
          <ReferenceTooltip
            record={existingValue}
            imageField={imageField}
            imageFieldPresentation={imageFieldPresentation}
            description={metadata}
            onView={() => {
              ref?.current?.close();
              onOpen();
              onView(module);
            }}
          />
        )}
      >
        <RecordNameCell
          record={existingValue}
          onOpen={() => {
            onOpen();
            onView(module);
          }}
          disableViewRecord={disableViewRecord}
          imageField={imageField || null}
          imageFieldPresentation={imageFieldPresentation}
          metadataField={metadataField}
          metadataFieldType={metadataFieldType}
          nameField={recordNameField}
          emptyText={noRecordNameValue}
        />
      </AppTooltip>
    </div>
  );
};

export default forwardRef(SingleReferenceRecord);
